import {
  AUTH_APP_AUTO_SELECT_STORAGE_KEY,
  AUTH_PORTFOLIO_PREVIOUS_ROUTE_STORAGE_KEY_PREFIX,
  COMPANY_VALUATIONS,
  LEGACY_COMPANY_VALUATIONS_AUTO_SELECT,
  PORTFOLIO_VALUATION,
} from 'common/constants/auth/appSwitch';
import { readTabSessionValue, removeTabSessionValue, writeTabSessionValue } from 'services/storage/tabSessionStorage';
import type { AuthAppAutoSelect } from './appSwitch.types';

const AUTH_APP_AUTO_SELECT_VALUES: readonly AuthAppAutoSelect[] = [PORTFOLIO_VALUATION, COMPANY_VALUATIONS];

export const isAuthAppAutoSelect = (value: string): value is AuthAppAutoSelect =>
  (AUTH_APP_AUTO_SELECT_VALUES as readonly string[]).includes(value);

// Product-switch intents and return routes belong to the tab that initiated the handoff.

export const saveAppAutoSelect = (app: AuthAppAutoSelect): void => {
  writeTabSessionValue(AUTH_APP_AUTO_SELECT_STORAGE_KEY, app);
};

export const clearAppAutoSelect = (): void => {
  removeTabSessionValue(AUTH_APP_AUTO_SELECT_STORAGE_KEY);
};

export const getAppAutoSelect = (): AuthAppAutoSelect | null => {
  const raw = readTabSessionValue(AUTH_APP_AUTO_SELECT_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  if (raw === LEGACY_COMPANY_VALUATIONS_AUTO_SELECT) {
    writeTabSessionValue(AUTH_APP_AUTO_SELECT_STORAGE_KEY, COMPANY_VALUATIONS);
    return COMPANY_VALUATIONS;
  }

  if (isAuthAppAutoSelect(raw)) {
    return raw;
  }

  clearAppAutoSelect();
  return null;
};

const portfolioRouteStorageKey = (userId?: number) =>
  userId
    ? `${AUTH_PORTFOLIO_PREVIOUS_ROUTE_STORAGE_KEY_PREFIX}_${userId}`
    : AUTH_PORTFOLIO_PREVIOUS_ROUTE_STORAGE_KEY_PREFIX;

export const savePreviousPortfolioRoute = (route: string, userId?: number): void => {
  writeTabSessionValue(portfolioRouteStorageKey(userId), route);
};

const getPreviousPortfolioRoute = (userId?: number): string | null =>
  readTabSessionValue(portfolioRouteStorageKey(userId));

export const clearPreviousPortfolioRoute = (userId?: number): void => {
  removeTabSessionValue(portfolioRouteStorageKey(userId));
};

export const getSafePreviousPortfolioRoute = (userId?: number): string | null => {
  const route = getPreviousPortfolioRoute(userId);

  if (!route) {
    return null;
  }

  const pathOnly = route.split('?')[0] ?? '';

  if (
    pathOnly.startsWith('/') &&
    !pathOnly.startsWith('//') &&
    !pathOnly.includes('/sign-in') &&
    /^\/(firms|funds|companies)\b/.test(pathOnly)
  ) {
    return route;
  }

  clearPreviousPortfolioRoute(userId);
  return null;
};
